/*
 * BUDB – Premium Poker Table
 * Green felt table with real card images, chip art & casino-quality UI
 * GRUDGE STUDIO
 */

import { useEffect, useState, useMemo, useCallback, memo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { REFRESH_INTERVALS, STALE_TIMES } from '@shared/constants';
import { useWallet } from '@/contexts/wallet-context';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { LoadingSkeleton } from '@/components/loading-skeleton';
import { CommunityCards, HoleCards, FoldedCards } from '@/components/animated-card';
import { tableEventBus } from '@/lib/table-event-bus';
import { useCardAnimations, usePotAnimation } from '@/hooks/use-table-events';
import { ArrowLeft, Wifi, WifiOff, Users, Plus, Minus, RefreshCw } from 'lucide-react';
import { usePokerWebSocket, type HandCompleteEvent, type BlindLevelEvent } from '@/hooks/use-poker-websocket';
import { CRAFTPIX, tablePreviewSrc, tableTheme } from '@/lib/craftpix-poker-ui';
import { useDisplay } from '@/hooks/use-display';
import { avatarUrl } from '@shared/player-appearance';
import { HamburgerButton, PhoneHamburgerMenu } from '@/components/phone-hamburger-menu';
import { casino } from '@/lib/casino-sfx';
import { ProBadgeRing } from '@/components/pro-badge-ring';
import { TableLeftRail, TableRightRail } from '@/components/table-side-rails';
import { BudbAvatarEmbed } from '@/components/budb-avatar-embed';
import { SpriteFont } from '@/components/sprite-font';
import type { SevensFontColor } from '@/lib/sprite-fonts';
import {
  listOpenTables,
  setOpenTableAction,
  upsertOpenTable,
  type OpenTableEntry,
} from '@/lib/open-tables';
import { BUDB_CHARACTER } from '@shared/budb-character';
import {
  betChipOffset,
  parsePct,
} from '@/lib/table-layout';
import { resolveTableLayout, fetchPublishedLayouts } from '@/lib/layout-runtime';
import { bestHandLabel } from '@/lib/hand-strength';
import { LeaveTableDialog } from '@/components/play-ledger-dialogs';
import { useWalletState } from '@/contexts/wallet-context';
import '@/styles/calling-pros.css';
import '@/styles/table-chrome.css';
import '@/styles/table-production.css';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Player {
  id: string;
  username: string;
  chips: number;
  position: number;
  isActive: boolean;
  /** True only when this seat must act (shot clock / time bank) */
  isToAct?: boolean;
  cards?: Array<{ rank: string; suit: string }>;
  currentBet?: number;
  isFolded?: boolean;
  isDealer?: boolean;
  isSmallBlind?: boolean;
  isBigBlind?: boolean;
  avatarId?: string;
  nameColor?: string;
  frameColor?: string;
  avatarScale?: number;
  /** Calling Pros badge — circle behind seat when claimed */
  proBadgeId?: string;
  timeBankMs?: number;
  /** Server: sitting out next hand / idle */
  sittingOut?: boolean;
}

interface TableState {
  id: string;
  name: string;
  maxPlayers: number;
  currentPlayers: number;
  status: string;
  gameState: {
    pot: number;
    currentBet: number;
    phase: string;
    communityCards: Array<{ rank: string; suit: string }>;
  };
  players: Player[];
  stakes: { smallBlind: number; bigBlind: number };
  isAIPractice?: boolean;
}

// ── Chip image helper — craftpix/poker2 denomination chips ───────────────────
const CHIP_VALUES = [10000, 5000, 1000, 500, 100, 50, 25, 10, 4, 1];
function chipImage(amount: number): string {
  const closest = CHIP_VALUES.find(v => v <= amount) ?? 1;
  return CRAFTPIX.chips.of(closest);
}

// Seat layouts: client/src/lib/table-layout.ts (off-felt rails, HU WSOP-style)

// ── Turn timer (server-authoritative shot clock + time bank) ─────────────────
interface TableTimer {
  phase: 'shot' | 'bank' | null;
  deadlineAt: number;
  remainingMs: number;
  phaseTotalMs?: number;
  shotClockMs: number;
  timeBankMs: number;
  timeBankMaxMs: number;
  actorSeat: number;
  actorId: string | null;
  serverNow: number;
}

function TurnTimerBar({
  active,
  deadlineAt,
  phase,
  totalMs,
  timeBankMs,
  large = false,
}: {
  active: boolean;
  /** Server epoch ms when phase ends */
  deadlineAt?: number | null;
  phase?: 'shot' | 'bank' | null;
  /** Duration of current phase for % bar */
  totalMs?: number;
  timeBankMs?: number;
  /** Phone dock: larger clock digits */
  large?: boolean;
}) {
  const [remainingMs, setRemainingMs] = useState(0);
  const [pct, setPct] = useState(100);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active || !deadlineAt) {
      setPct(100);
      setRemainingMs(0);
      return;
    }
    const total = Math.max(1, totalMs ?? 15_000);
    const tick = () => {
      const left = Math.max(0, deadlineAt - Date.now());
      setRemainingMs(left);
      setPct(Math.max(0, Math.min(100, (left / total) * 100)));
      if (left > 0) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, deadlineAt, totalMs]);

  if (!active) return null;

  const color =
    phase === 'bank'
      ? '#f59e0b'
      : pct > 50
        ? '#22c55e'
        : pct > 25
          ? '#f59e0b'
          : '#ef4444';
  const secs = Math.ceil(remainingMs / 1000);
  const digitH = large ? 22 : 16;
  const fontColor: SevensFontColor =
    phase === 'bank' ? 'yellow' : pct > 50 ? 'green' : pct > 25 ? 'yellow' : 'red';

  return (
    <div
      className="poker-shot-clock"
      style={{ width: large ? 112 : 96, marginTop: 4 }}
      data-phase={phase || 'shot'}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 3,
          gap: 4,
        }}
      >
        <span
          style={{
            fontSize: large ? 10 : 9,
            fontWeight: 800,
            letterSpacing: '0.08em',
            color,
            fontFamily: '"Bebas Neue", "Arial Narrow", Impact, sans-serif',
          }}
        >
          {phase === 'bank' ? 'BANK' : 'ACT'}
        </span>
        <SpriteFont
          text={`${secs}`}
          color={fontColor}
          height={digitH}
          tracking={0.02}
          ariaLabel={`${secs} seconds`}
        />
      </div>
      <div style={{ position: 'relative', width: '100%', height: large ? 14 : 12 }}>
        <img
          src={CRAFTPIX.game.timePanel}
          alt=""
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'fill' }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 2,
            top: 2,
            bottom: 2,
            width: `calc(${pct}% - 4px)`,
            overflow: 'hidden',
            borderRadius: 2,
            background: color,
            opacity: 0.9,
            boxShadow: `0 0 8px ${color}`,
            transition: 'width 0.1s linear',
          }}
        />
      </div>
      {typeof timeBankMs === 'number' && timeBankMs > 0 && phase !== 'bank' && (
        <div
          style={{
            marginTop: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontFamily: '"Bebas Neue", "Arial Narrow", Impact, sans-serif',
            fontSize: large ? 12 : 11,
            color: 'rgba(251,191,36,0.9)',
            letterSpacing: '0.04em',
          }}
        >
          BANK
          <SpriteFont
            text={`${Math.ceil(timeBankMs / 1000)}`}
            color="yellow"
            height={large ? 14 : 12}
            tracking={0.02}
          />
        </div>
      )}
    </div>
  );
}

// ── PlayerSeat — rail off felt; hole cards + compact stack; bets drawn on felt separately ──
const PlayerSeat = memo(function PlayerSeat({
  player,
  isHero,
  seatPos,
  isDealing,
  heroPublicKey,
  compact = false,
  uiScale = 1,
  timer = null,
  hideHole = false,
}: {
  player: Player | null;
  isHero: boolean;
  seatPos: { left: string; top: string };
  isDealing: boolean;
  timer?: TableTimer | null;
  heroPublicKey?: string;
  compact?: boolean;
  uiScale?: number;
  /** Phone: hero cards live in action dock */
  hideHole?: boolean;
}) {
  const isEmpty = !player;
  const emptySz = compact ? 44 : 52;
  const playerScale = player?.avatarScale && player.avatarScale > 0 ? player.avatarScale : 1;
  const avatarSz = Math.round((compact ? 26 : 32) * playerScale);
  const scale = Math.max(0.78, Math.min(uiScale, 1.08));
  const nameColor = player?.nameColor || '#ffffff';
  const frameColor = player?.frameColor || 'rgba(255,255,255,0.25)';
  const faceSrc = player?.avatarId
    ? avatarUrl(player.avatarId)
    : CRAFTPIX.game.playerAvatar;

  return (
    <div
      className="poker-seat"
      data-empty={isEmpty ? '1' : '0'}
      style={{
        position: 'absolute',
        left: seatPos.left,
        top: seatPos.top,
        transform: `translate(-50%, -50%) scale(${scale})`,
        transformOrigin: 'center center',
        zIndex: isEmpty ? 2 : 8,
        minWidth: compact ? 64 : 78,
      }}
    >
      {isEmpty ? (
        <div
          style={{
            width: emptySz,
            height: emptySz,
            borderRadius: '50%',
            border: '1.5px dashed rgba(255,255,255,0.16)',
            background: 'rgba(0,0,0,0.28)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Users size={compact ? 14 : 18} color="rgba(255,255,255,0.18)" />
        </div>
      ) : (
        <motion.div
          className="poker-seat-rail"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: player.isFolded ? 0.42 : 1 }}
          transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        >
          {!hideHole && (
            <div style={{ marginBottom: 2 }}>
              {player.isFolded ? (
                <FoldedCards size="sm" />
              ) : (
                <HoleCards
                  cards={(player.cards ?? []).map((c) => ({
                    suit: c.suit,
                    value: c.rank,
                  }))}
                  isHidden={player.id !== heroPublicKey}
                  isDealing={isDealing}
                  size={isHero ? 'md' : 'sm'}
                />
              )}
            </div>
          )}

          <div
            className="poker-seat-panel"
            data-hero={isHero ? '1' : '0'}
            data-act={player.isToAct ? '1' : '0'}
          >
            <div
              style={{
                position: 'relative',
                width: avatarSz + 10,
                height: avatarSz + 10,
                margin: '0 auto 2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ProBadgeRing
                proId={player.proBadgeId}
                seatIndex={player.position}
                size={avatarSz + 10}
                claimed={Boolean(player.proBadgeId)}
                adminHold={!player.proBadgeId}
              />
              <img
                src={faceSrc}
                alt=""
                style={{
                  position: 'absolute',
                  width: avatarSz * 0.7,
                  height: avatarSz * 0.7,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  zIndex: 4,
                  border: `1.5px solid ${frameColor}`,
                }}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  if (!img.src.includes('cute/01')) img.src = '/avatars/cute/01.png';
                  else img.src = CRAFTPIX.game.playerAvatar;
                }}
              />
            </div>
            <div
              style={{
                color: nameColor,
                fontSize: 10,
                fontWeight: 700,
                maxWidth: 72,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                margin: '0 auto',
              }}
            >
              {player.username?.slice(0, 10) || `P${player.position}`}
            </div>
            {player.sittingOut && (
              <div
                style={{
                  fontSize: 8,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  color: '#fbbf24',
                  background: 'rgba(0,0,0,0.65)',
                  border: '1px solid rgba(251,191,36,0.45)',
                  borderRadius: 4,
                  padding: '1px 4px',
                  marginTop: 2,
                  fontFamily: '"Bebas Neue", Impact, sans-serif',
                }}
              >
                SIT OUT
              </div>
            )}
            <TurnTimerBar
              active={Boolean(player.isToAct)}
              deadlineAt={player.isToAct ? timer?.deadlineAt : null}
              phase={player.isToAct ? timer?.phase : null}
              totalMs={
                player.isToAct
                  ? timer?.phaseTotalMs ?? timer?.shotClockMs
                  : undefined
              }
              timeBankMs={player.timeBankMs}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                marginTop: 2,
              }}
            >
              <img
                src={chipImage(player.chips)}
                alt=""
                style={{ width: 14, height: 14 }}
              />
              <span style={{ color: '#fbbf24', fontSize: 11, fontWeight: 800 }}>
                {player.chips?.toFixed(0)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                marginTop: 2,
                flexWrap: 'wrap',
              }}
            >
              {player.isDealer && (
                <img
                  src={CRAFTPIX.generated.dealerButton}
                  alt="D"
                  title="Dealer"
                  data-testid="dealer-button"
                  style={{
                    width: 18,
                    height: 18,
                    objectFit: 'contain',
                    borderRadius: '50%',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              {player.isSmallBlind && (
                <span
                  style={{
                    fontSize: 8,
                    fontWeight: 800,
                    color: '#93c5fd',
                    background: 'rgba(37,99,235,0.35)',
                    borderRadius: 3,
                    padding: '0 3px',
                  }}
                >
                  SB
                </span>
              )}
              {player.isBigBlind && (
                <span
                  style={{
                    fontSize: 8,
                    fontWeight: 800,
                    color: '#e9d5ff',
                    background: 'rgba(126,34,206,0.4)',
                    borderRadius: 3,
                    padding: '0 3px',
                  }}
                >
                  BB
                </span>
              )}
              {player.isFolded && (
                <span
                  style={{
                    fontSize: 8,
                    fontWeight: 800,
                    color: '#fecaca',
                    background: 'rgba(185,28,28,0.45)',
                    borderRadius: 3,
                    padding: '0 3px',
                  }}
                >
                  FOLD
                </span>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
});

/** In-play bet stack on the felt (between seat and pot) */
function BetOnFelt({
  amount,
  seatPos,
  isHu,
}: {
  amount: number;
  seatPos: { left: string; top: string };
  isHu: boolean;
}) {
  if (!amount || amount <= 0) return null;
  const pos = betChipOffset(parsePct(seatPos.left), parsePct(seatPos.top), isHu);
  return (
    <motion.div
      className="poker-bet-on-felt"
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{ left: pos.left, top: pos.top }}
    >
      <img src={chipImage(amount)} alt="" width={22} height={22} style={{ filter: 'drop-shadow(0 2px 4px #000)' }} />
      <span className="poker-bet-on-felt__amt">{amount.toFixed(0)}</span>
    </motion.div>
  );
}

// ── Action Button — font-only (no craftpix word-art sprites) ─────────────────
function ActionBtn({
  label, onClick, disabled, color, testId, sublabel,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  color: string;
  testId: string;
  sublabel?: string;
}) {
  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      whileHover={disabled ? {} : { scale: 1.04 }}
      whileTap={disabled ? {} : { scale: 0.97 }}
      data-testid={testId}
      style={{
        minWidth: 72,
        minHeight: 40,
        padding: sublabel ? '6px 12px 5px' : '0 12px',
        border: `1px solid ${disabled ? 'rgba(255,255,255,0.08)' : color + '99'}`,
        borderRadius: 10,
        background: disabled
          ? 'rgba(20,24,36,0.85)'
          : `linear-gradient(180deg, ${color}ee 0%, ${color}99 100%)`,
        boxShadow: disabled ? 'none' : `0 4px 14px ${color}44`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.42 : 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <span style={{
        color: '#fff',
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        fontFamily: 'Inter, system-ui, sans-serif',
        textShadow: '0 1px 2px rgba(0,0,0,0.55)',
        lineHeight: 1.1,
      }}>
        {label}
      </span>
      {sublabel != null && sublabel !== '' && (
        <span style={{
          color: 'rgba(255,255,255,0.92)',
          fontSize: 12,
          fontWeight: 900,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {sublabel}
        </span>
      )}
    </motion.button>
  );
}

// ── Main Table Component ───────────────────────────────────────────────────────
export default function Table(): JSX.Element | null {
  // Dynamic table id from /table/:tableId (production multi-table lobby)
  const [pathLoc] = useLocation();
  const tableId = useMemo(() => {
    const m = pathLoc.match(/^\/table\/([^/?#]+)/);
    return m?.[1] || 'table-1';
  }, [pathLoc]);

  // Single numeric value used for both bet and raise slider
  const [betValue, setBetValue] = useState(0);
  const [handResultMsg, setHandResultMsg] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // WS auth token — issued by the server's REST join endpoint, stored in sessionStorage
  const [wsToken, setWsToken] = useState<string | undefined>(() => {
    try {
      const m = window.location.pathname.match(/^\/table\/([^/?#]+)/);
      const id = m?.[1] || 'table-1';
      return sessionStorage.getItem(`ws_token_${id}`) ?? undefined;
    } catch {
      return undefined;
    }
  });
  const { publicKey, connected } = useWallet();
  const { refreshBalances } = useWalletState();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const prevCommunityCardsRef = useRef<number>(0);
  const heroWallet = publicKey?.toString();
  const display = useDisplay();
  const [leaveOpen, setLeaveOpen] = useState(false);

  // Re-load token when table changes
  useEffect(() => {
    try {
      setWsToken(sessionStorage.getItem(`ws_token_${tableId}`) ?? undefined);
    } catch {
      setWsToken(undefined);
    }
  }, [tableId]);

  // Stable callbacks for WS events
  const onHandComplete = useCallback((event: HandCompleteEvent) => {
    const msg = event.isTie
      ? `Split pot! ${event.tiedPlayers?.join(' & ')} — ${event.handRank}`
      : `${event.winnerName} wins ${event.potAmount} GBUX with ${event.handRank}`;
    setHandResultMsg(msg);
    casino.win();
    setTimeout(() => setHandResultMsg(null), 4000);
  }, []);
  const onBlindLevelUp = useCallback((event: BlindLevelEvent) => {
    toast({ title: `Blinds Up — Level ${event.level}`, description: `${event.sb}/${event.bb} GBUX`, variant: 'default' });
  }, [toast]);
  const onWsError = useCallback((msg: string) => {
    console.warn('WS error:', msg);
  }, []);

  // ── WebSocket real-time connection ──────────────────────────────────────────
  const {
    gameState: wsGameState,
    lastAction: wsLastAction,
    chatMessages: wsChatMessages,
    connected: wsConnected,
    authenticated: wsAuthenticated,
    sendAction: wsSendAction,
    sendChat: wsSendChat,
    sendSitOut: wsSendSitOut,
  } = usePokerWebSocket({
    tableId,
    wsToken,
    onHandComplete,
    onBlindLevelUp,
    onError: onWsError,
  });

  const [openTables, setOpenTables] = useState<OpenTableEntry[]>(() =>
    listOpenTables(),
  );

  // ── REST polling fallback (still used when WS hasn't delivered yet) ─────────
  const { data: tableData, isLoading } = useQuery<{ table: TableState }>({
    queryKey: ['/api/tables', tableId],
    queryFn: async () => {
      const r = await fetch(`/api/tables/${tableId}`);
      if (!r.ok) throw new Error('table fetch failed');
      return r.json();
    },
    refetchInterval: wsConnected ? 10000 : REFRESH_INTERVALS.GAMEPLAY, // slower when WS active
    staleTime: STALE_TIMES.GAMEPLAY,
    enabled: connected,
  });

  const { data: userProfile } = useQuery<{ user: any }>({
    queryKey: ['/api/user/profile', heroWallet],
    enabled: connected && !!heroWallet,
    refetchInterval: REFRESH_INTERVALS.USER_PROFILE,
    staleTime: STALE_TIMES.USER_PROFILE,
  });

  const table = useMemo(() => tableData?.table, [tableData]);
  const user = useMemo(() => userProfile?.user, [userProfile]);

  // Published / draft seat rails from /admin layout studio
  const [layoutRev, setLayoutRev] = useState(0);
  useEffect(() => {
    void fetchPublishedLayouts().then(() => setLayoutRev((n) => n + 1));
  }, []);
  const maxPlayers = table?.maxPlayers || 6;
  const seatPositions = useMemo(
    () => resolveTableLayout(maxPlayers, display.useVerticalTable),
    [maxPlayers, display.useVerticalTable, layoutRev],
  );

  // Prefer WS game state; fall back to REST-shaped state
  const effectiveSeats = useMemo(() => {
    if (wsGameState?.seats) return wsGameState.seats;
    return table?.players?.map((p, i) => ({
      position: i,
      playerId: p.id,
      playerName: p.username,
      chips: p.chips,
      currentBet: p.currentBet ?? 0,
      isActive: p.isActive,
      isFolded: p.isFolded ?? false,
      isDealer: p.isDealer ?? false,
      isSmallBlind: p.isSmallBlind ?? false,
      isBigBlind: p.isBigBlind ?? false,
      holeCards: (p.cards ?? []).map((c: any) => ({ suit: c.suit, rank: c.rank, value: 0, unicode: '' })),
      totalBetThisRound: 0,
      isAllIn: false,
      totalContributed: 0,
      isSittingOut: false,
    })) ?? [];
  }, [wsGameState?.seats, table?.players]);

  const gameState = useMemo(() => ({
    pot: wsGameState?.pot ?? table?.gameState?.pot ?? 0,
    currentBet: wsGameState?.currentBet ?? table?.gameState?.currentBet ?? 0,
    communityCards: wsGameState?.communityCards ?? table?.gameState?.communityCards ?? [],
    phase: (wsGameState as any)?.round ?? table?.gameState?.phase ?? 'waiting',
    activePlayerIndex: wsGameState?.activePlayerIndex ?? 0,
    handInProgress: wsGameState?.handInProgress ?? false,
    minRaise: wsGameState?.minRaise ?? 0,
    timer: ((wsGameState as any)?.timer ?? null) as TableTimer | null,
  }), [wsGameState, table?.gameState]);

  const heroSeat = useMemo(
    () => effectiveSeats.find((s: any) => s.playerId === heroWallet),
    [effectiveSeats, heroWallet],
  );
  const isPlayerAtTable = useMemo(() => !!heroSeat, [heroSeat]);
  const isMyTurn = useMemo(() => {
    if (!wsGameState || !heroSeat) return false;
    return wsGameState.seats[wsGameState.activePlayerIndex]?.playerId === heroWallet;
  }, [wsGameState, heroSeat, heroWallet]);

  // Multi-table left rail — register seated tables + pulse when action on us
  useEffect(() => {
    if (!isPlayerAtTable || !table) return;
    upsertOpenTable({
      tableId,
      name: table.name || tableId,
      smallBlind: table.stakes?.smallBlind ?? 0,
      bigBlind: table.stakes?.bigBlind ?? 0,
      players: table.currentPlayers,
      maxPlayers: table.maxPlayers,
      actionOnUs: isMyTurn,
    });
    setOpenTableAction(tableId, isMyTurn);
    setOpenTables(listOpenTables());
  }, [
    isPlayerAtTable,
    isMyTurn,
    tableId,
    table?.name,
    table?.stakes?.smallBlind,
    table?.stakes?.bigBlind,
    table?.currentPlayers,
    table?.maxPlayers,
  ]);

  const cardAnimations = useCardAnimations(tableId);
  const potAnimation = usePotAnimation(tableId);

  useEffect(() => {
    if (wsGameState) {
      tableEventBus.processStateUpdate(tableId, {
        pot: wsGameState.pot,
        round: (wsGameState as any).round ?? 'waiting',
        communityCards: wsGameState.communityCards,
        seats: wsGameState.seats,
        handInProgress: wsGameState.handInProgress,
        currentBet: wsGameState.currentBet,
        activePlayerIndex: wsGameState.activePlayerIndex,
      });
    }
  }, [wsGameState, tableId]);

  const newCardIndices = useMemo(() => {
    const current = gameState.communityCards.length;
    const prev = prevCommunityCardsRef.current;
    const indices: number[] = [];
    if (current > prev) for (let i = prev; i < current; i++) indices.push(i);
    prevCommunityCardsRef.current = current;
    return indices;
  }, [gameState.communityCards.length]);

  // Kenney casino SFX — board deals, phase changes
  const prevPhaseRef = useRef<string>('');
  const prevHandRef = useRef(false);
  useEffect(() => {
    casino.unlock();
  }, []);
  useEffect(() => {
    const n = newCardIndices.length;
    if (n > 0) {
      if (n >= 3) casino.deal(n);
      else casino.dealBoard();
    }
  }, [newCardIndices]);
  useEffect(() => {
    const phase = gameState.phase;
    const inHand = gameState.handInProgress;
    if (inHand && !prevHandRef.current) {
      casino.shuffle();
      window.setTimeout(() => casino.deal(2), 200);
    }
    if (phase !== prevPhaseRef.current && phase === 'preflop' && inHand) {
      /* hole dealt with hand start */
    }
    prevPhaseRef.current = phase;
    prevHandRef.current = inHand;
  }, [gameState.phase, gameState.handInProgress]);

  // ── Action handlers ─────────────────────────────────────────────────────────
  const performActionMutation = useMutation({
    mutationFn: async (action: { type: string; amount?: number }) => {
      // Try WS first for lower latency; fall back to REST
      if (wsConnected && wsAuthenticated) {
        wsSendAction(action.type, action.amount);
        return { success: true };
      }
      const res = await fetch(`/api/tables/${tableId}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress: heroWallet,
          action: action.type,
          amount: action.amount ?? 0,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Action failed');
      return res.json();
    },
    onSuccess: (_data, action) => {
      if (action?.type === 'fold') casino.fold();
      else if (action?.type === 'check') casino.check();
      else if (action?.type === 'call') casino.call();
      else if (action?.type === 'bet') casino.bet();
      else if (action?.type === 'raise' || action?.type === 'allin') casino.raise();
      queryClient.invalidateQueries({ queryKey: ['/api/tables'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tables', tableId] });
      setBetValue(0);
    },
    onError: (error: Error) => {
      toast({ title: 'Action Failed', description: error.message, variant: 'destructive' });
    },
  });

  const handleFold   = useCallback(() => performActionMutation.mutate({ type: 'fold' }), [performActionMutation]);
  const handleCheck  = useCallback(() => performActionMutation.mutate({ type: 'check' }), [performActionMutation]);
  const handleCall   = useCallback(() => performActionMutation.mutate({ type: 'call' }), [performActionMutation]);
  const handleAllIn  = useCallback(() => performActionMutation.mutate({ type: 'allin' }), [performActionMutation]);
  const handleBet    = useCallback(() => {
    if (!betValue || betValue <= 0) {
      toast({ title: 'Set a bet amount', description: 'Use the slider or presets', variant: 'destructive' });
      return;
    }
    performActionMutation.mutate({ type: 'bet', amount: betValue });
  }, [betValue, performActionMutation, toast]);
  const handleRaise  = useCallback(() => {
    if (!betValue || betValue <= gameState.currentBet) {
      toast({ title: 'Invalid Raise', description: 'Raise must exceed current bet', variant: 'destructive' });
      return;
    }
    performActionMutation.mutate({ type: 'raise', amount: betValue });
  }, [betValue, gameState.currentBet, performActionMutation, toast]);

  useEffect(() => { if (!connected) setLocation('/connect'); }, [connected, setLocation]);

  if (!connected) return null;
  if (isLoading && !wsGameState) return <LoadingSkeleton variant="table" />;

  const maxSeats = seatPositions.length;

  // Absolute seat array (server order — do not change game logic)
  const absoluteSeats: (Player | null)[] = Array(maxSeats).fill(null);
  effectiveSeats.forEach((s: any, idx: number) => {
    if (idx < maxSeats && s.isActive) {
      const isToAct = Boolean(
        s.isToAct ??
          s.toAct ??
          (gameState.handInProgress && gameState.activePlayerIndex === idx),
      );
      absoluteSeats[idx] = {
        id: s.playerId,
        username: s.playerName,
        chips: s.chips,
        position: idx,
        isActive: s.isActive,
        isToAct,
        cards: s.holeCards?.map((c: any) => ({ rank: c.rank, suit: c.suit })),
        currentBet: s.currentBet,
        isFolded: s.isFolded,
        isDealer: s.isDealer,
        isSmallBlind: s.isSmallBlind,
        isBigBlind: s.isBigBlind,
        avatarId: s.avatarId,
        nameColor: s.nameColor,
        frameColor: s.frameColor,
        avatarScale: s.avatarScale,
        proBadgeId: s.proBadgeId,
        timeBankMs: s.timeBankMs,
        sittingOut: Boolean(s.sittingOut),
      };
    }
  });

  /**
   * Visual-only rotation: each player sees themselves at bottom (index 0).
   * Seat numbers / dealer / action order stay absolute on the server.
   */
  const heroAbsIdx = absoluteSeats.findIndex((p) => p?.id === heroWallet);
  const rotateBy = heroAbsIdx >= 0 ? heroAbsIdx : 0;
  const seats: (Player | null)[] = Array.from({ length: maxSeats }, (_, visualI) => {
    const absI = (rotateBy + visualI) % maxSeats;
    return absoluteSeats[absI] ?? null;
  });

  const phaseColors: Record<string, string> = {
    preflop: '#6366f1', flop: '#0ea5e9', turn: '#f59e0b', river: '#ef4444', showdown: '#ec4899', waiting: '#6b7280',
  };
  const phaseColor = phaseColors[gameState.phase] || '#6b7280';

  const isPending = performActionMutation.isPending;
  const isWaiting = gameState.phase === 'waiting' || !gameState.handInProgress;
  const canAct = isMyTurn && !isWaiting && !isPending;

  // ── Poker math derived values ─────────────────────────────────────────────
  const heroCurrentBet = (heroSeat as any)?.currentBet ?? 0;
  const heroChips      = (heroSeat as any)?.chips ?? 0;
  const bigBlind       = table?.stakes?.bigBlind ?? 2;
  const toCall         = Math.max(0, gameState.currentBet - heroCurrentBet);
  const callAmount     = Math.min(toCall, heroChips);
  const isAllInCall    = toCall > 0 && toCall >= heroChips;
  const hasToCall      = toCall > 0;
  const canCheck       = !hasToCall;
  const minRaiseAmt    = gameState.minRaise > 0
    ? gameState.minRaise
    : Math.max(gameState.currentBet * 2, bigBlind * 2);
  const sliderMin      = Math.max(1, hasToCall ? minRaiseAmt : bigBlind);
  const sliderMax      = Math.max(heroChips, sliderMin + 1);
  const effectiveBet   = betValue > 0 ? betValue : sliderMin;
  const pot            = gameState.pot;
  const potPresets = [
    { label: '⅓P', amt: Math.round(pot / 3) },
    { label: '½P',  amt: Math.round(pot / 2) },
    { label: '⅔P', amt: Math.round((pot * 2) / 3) },
    { label: 'Pot', amt: pot },
    { label: '2×',  amt: pot * 2 },
  ].map(p => ({ ...p, amt: Math.min(heroChips, Math.max(sliderMin, p.amt)) }))
   .filter((p, i, arr) => p.amt > 0 && (i === 0 || p.amt !== arr[i - 1].amt));
  const potOdds = hasToCall && (pot + callAmount) > 0
    ? Math.round((callAmount / (pot + callAmount)) * 100)
    : 0;

  const phone = display.useVerticalTable;
  const heroPlayer = seats[0];
  const isHu = (table?.maxPlayers ?? 6) <= 2;
  const stepAmt = Math.max(1, Math.round(bigBlind / 2));
  const raiseLabel = hasToCall
    ? `RAISE TO ${effectiveBet.toFixed(0)}`
    : `BET ${effectiveBet.toFixed(0)}`;

  // PokerStars-style best hand — only for local hero who can see hole cards
  const heroBestHand =
    isPlayerAtTable && heroPlayer && !heroPlayer.isFolded
      ? bestHandLabel(
          (heroPlayer.cards ?? []).map((c) => ({
            rank: c.rank,
            suit: c.suit,
          })),
          gameState.communityCards.map((c) => ({
            rank: c.rank,
            suit: c.suit,
          })),
        )
      : null;

  const requestLeaveOrLobby = () => {
    if (isPlayerAtTable && heroWallet) {
      setLeaveOpen(true);
      return;
    }
    setLocation('/lobby');
  };

  const joinSimilar = () => {
    requestLeaveOrLobby();
  };

  const phoneBetPresets = [
    { label: '2.5×', amt: Math.min(heroChips, Math.max(sliderMin, Math.round(bigBlind * 2.5))) },
    { label: '3×', amt: Math.min(heroChips, Math.max(sliderMin, bigBlind * 3)) },
    { label: '4×', amt: Math.min(heroChips, Math.max(sliderMin, bigBlind * 4)) },
    { label: 'All-in', amt: heroChips },
  ];

  return (
    <div
      className={`poker-table-page poker-table-page--chrome poker-table-page--prod${phone ? ' poker-table-page--phone' : ''}${isHu ? ' poker-table-page--hu' : ''}`}
      style={{
        minHeight: '100dvh',
        display: phone ? 'flex' : undefined,
        flexDirection: phone ? 'column' : undefined,
        paddingBottom: phone ? 0 : 'env(safe-area-inset-bottom)',
        overflow: phone ? 'hidden' : undefined,
      }}
    >
      <PhoneHamburgerMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {heroWallet && (
        <LeaveTableDialog
          open={leaveOpen}
          onClose={() => setLeaveOpen(false)}
          tableId={tableId}
          wallet={heroWallet}
          stackHint={heroChips}
          onLeft={(cashedOut, balance) => {
            void refreshBalances();
            void queryClient.invalidateQueries({ queryKey: ['/api/user/profile'] });
            toast({
              title: cashedOut > 0 ? 'Stack returned to play' : 'Left table',
              description:
                cashedOut > 0
                  ? `+${cashedOut.toLocaleString()} GBUX → play ledger · balance ${balance.toLocaleString()}`
                  : 'No chips to return',
            });
            setLocation('/lobby');
          }}
        />
      )}

      {/* ── Hand result overlay ── */}
      {handResultMsg && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed', top: 'max(72px, env(safe-area-inset-top))', left: '50%', transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, rgba(234,179,8,0.95), rgba(180,120,0,0.95))',
            color: '#000', borderRadius: 16, padding: '12px 28px',
            fontWeight: 800, fontSize: 15, zIndex: 200,
            boxShadow: '0 4px 30px rgba(234,179,8,0.5)',
            textAlign: 'center', maxWidth: 'min(400px, 92vw)',
          }}
        >
          {handResultMsg}
        </motion.div>
      )}

      {!phone && (
        <TableLeftRail
          tableId={tableId}
          actionOnUs={isMyTurn}
          lastAction={wsLastAction}
          chatMessages={wsChatMessages}
          selfWallet={heroWallet}
          communityCards={gameState.communityCards}
          phase={gameState.phase}
          handResult={handResultMsg}
          sendChat={wsSendChat}
          wsConnected={wsConnected && wsAuthenticated}
          openTables={openTables}
          onRefreshOpen={() => setOpenTables(listOpenTables())}
        />
      )}

      {/* ── Header: phone = hamburger + logo + stakes + Join; desktop = lobby back ── */}
      <div className="poker-table-header" style={{
        background: phone ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.65)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(10px)',
        padding: phone ? '8px 10px' : '10px 14px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 8, flexWrap: phone ? 'nowrap' : 'wrap',
        paddingTop: 'max(8px, env(safe-area-inset-top))',
        zIndex: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: phone ? 8 : 10, minWidth: 0, flex: 1 }}>
          {phone ? (
            <HamburgerButton onClick={() => setMenuOpen(true)} />
          ) : (
            <button onClick={requestLeaveOrLobby} data-testid="button-back" style={{
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 8, color: 'white', padding: '8px 12px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, minHeight: 40,
            }}>
              <ArrowLeft size={14} /> {isPlayerAtTable ? 'Leave' : 'Lobby'}
            </button>
          )}
          {phone ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <img
                src={CRAFTPIX.generated.logoMark}
                alt="BUDB PKR"
                style={{ height: 28, objectFit: 'contain', borderRadius: 4 }}
                onError={(e) => { (e.target as HTMLImageElement).src = CRAFTPIX.generated.appIcon; }}
              />
              <div style={{ minWidth: 0 }}>
                <div className="poker-table-meta" style={{ marginBottom: 2 }}>
                  <span>
                    <b>
                      {table?.stakes?.smallBlind}/{table?.stakes?.bigBlind}
                    </b>{' '}
                    GBUX
                  </span>
                  <span>·</span>
                  <span>
                    {isHu ? 'Heads-Up' : 'Hold\'em'}
                  </span>
                  <span>·</span>
                  <span>
                    {effectiveSeats.filter((s: any) => s.isActive).length}/
                    {table?.maxPlayers || 6}
                  </span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                  {gameState.phase}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ minWidth: 0 }}>
              <div data-testid="text-table-name" style={{ color: 'white', fontWeight: 700, fontSize: 15, letterSpacing: 0.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {table?.name || 'BetUDontBet Poker'}
              </div>
              <div className="poker-table-meta">
                <span data-testid="text-stakes-display">
                  <b>
                    {table?.stakes?.smallBlind}/{table?.stakes?.bigBlind}
                  </b>{' '}
                  GBUX
                </span>
                <span>·</span>
                <span data-testid="text-player-count">
                  {effectiveSeats.filter((s: any) => s.isActive).length}/
                  {table?.maxPlayers || 6}
                </span>
                <span>·</span>
                <span data-testid="text-game-type">{isHu ? 'HU' : "Hold'em"}</span>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {!phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: wsConnected ? '#4ade80' : '#f87171' }}>
              {wsConnected ? <Wifi size={13} /> : <WifiOff size={13} />}
              <span className="hide-xs">{wsConnected ? 'Live' : 'Polling'}</span>
            </div>
          )}
          {phone ? (
            <button
              type="button"
              onClick={joinSimilar}
              data-testid="button-join-similar"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
                border: 'none', borderRadius: 20, color: '#fff',
                padding: '8px 12px', fontWeight: 700, fontSize: 12,
                minHeight: 40, cursor: 'pointer', whiteSpace: 'nowrap',
                boxShadow: '0 2px 12px rgba(59,130,246,0.35)',
              }}
            >
              Join Similar <Plus size={14} strokeWidth={3} />
            </button>
          ) : (
            <>
              <motion.div key={gameState.phase} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                style={{
                  background: phaseColor, color: 'white', borderRadius: 20,
                  padding: '4px 12px', fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
                  textTransform: 'uppercase', boxShadow: `0 0 14px ${phaseColor}55`,
                }}
                data-testid="badge-phase"
              >
                {gameState.phase}
              </motion.div>
              {user && (
                <div style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '4px 10px', textAlign: 'right' }}>
                  <div style={{ color: '#4ade80', fontWeight: 700, fontSize: 13 }} data-testid="text-balance">
                    {Number(heroChips || user.gbuxBalance || 0).toLocaleString()}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Table viewport (+ desktop rails wrap) ── */}
      <div
        className={`poker-table-chrome-main poker-table-viewport${phone ? ' poker-table-viewport--vertical' : ''}`}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: phone ? '2px 0 0' : '8px 8px 0',
          width: '100%',
          minHeight: 0,
        }}
      >
        <div
          className={`poker-table-felt${phone ? ' poker-table-felt--vertical' : ''}${isHu ? ' poker-table-felt--hu' : ''}`}
          data-testid="card-poker-table"
          data-layout={phone ? 'vertical' : 'landscape'}
          data-format={isHu ? 'hu' : 'nl'}
        >
          <img
            className="poker-table-felt__img"
            src={
              isHu
                ? CRAFTPIX.generated.feltBlue
                : phone
                  ? CRAFTPIX.generated.feltVerticalPhone
                  : table?.stakes?.bigBlind != null
                    ? tablePreviewSrc(
                        tableTheme({
                          isAI: Boolean(
                            (table as { isAIPractice?: boolean }).isAIPractice,
                          ),
                          isHu: false,
                          bigBlind: table.stakes.bigBlind,
                        }),
                      )
                    : CRAFTPIX.generated.feltGreen
            }
            alt={isHu ? 'BUDB heads-up table' : 'BUDB poker table'}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.src = CRAFTPIX.generated.feltBlue;
            }}
          />
          <div className="poker-table-felt__vignette" aria-hidden />

          {/* phonepoker3: gold logo watermark center of oval */}
          {phone && (
            <img
              className="poker-table-felt__logo"
              src={CRAFTPIX.generated.logoMark}
              alt=""
              aria-hidden
              draggable={false}
            />
          )}

          {/* Board + pot only on felt (phone: upper cyan zone) */}
          <div className={`poker-board-zone${phone ? ' poker-board-zone--phone' : ''}`}>
            <motion.div
              key={gameState.pot}
              className="poker-pot-pill"
              initial={{ scale: 1.12, opacity: 0.75 }}
              animate={{ scale: 1, opacity: 1 }}
            >
              POT{' '}
              {gameState.pot % 1 === 0
                ? gameState.pot.toFixed(0)
                : gameState.pot.toFixed(2)}
              {gameState.currentBet > 0 && (
                <span style={{ color: '#4ade80', fontWeight: 700, fontSize: 11 }}>
                  · to {gameState.currentBet.toFixed(0)}
                </span>
              )}
            </motion.div>

            {isMyTurn && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                  background: 'rgba(234,179,8,0.18)',
                  border: '1px solid rgba(234,179,8,0.45)',
                  borderRadius: 8,
                  padding: '2px 10px',
                  color: '#fbbf24',
                  fontSize: 10,
                  fontWeight: 800,
                  pointerEvents: 'auto',
                }}
              >
                YOUR TURN
                {gameState.timer?.deadlineAt
                  ? ` · ${Math.max(0, Math.ceil((gameState.timer.deadlineAt - Date.now()) / 1000))}s`
                  : ''}
              </motion.div>
            )}

            <div className={phone ? 'poker-board-zone__cards' : undefined}>
              <CommunityCards
                cards={gameState.communityCards.map((c: any) => ({
                  suit: c.suit,
                  value: c.rank,
                }))}
                isDealing={cardAnimations.isDealing}
                newCardIndices={newCardIndices}
              />
            </div>
          </div>

          {/* Bets on felt lanes (not inside seat chrome) */}
          {seats.map((player, idx) => {
            if (!player?.currentBet || player.currentBet <= 0) return null;
            const sp = seatPositions[idx] ?? seatPositions[0]!;
            return (
              <BetOnFelt
                key={`bet-${player.id}`}
                amount={player.currentBet}
                seatPos={sp}
                isHu={isHu}
              />
            );
          })}

          {/*
            Seats: phone keeps hero ON bottom rim (avatar + stack, hideHole —
            hole cards live in dock below). Opponents sit on brass-stud ring.
            Browser: all seats on landscape rails with holes on seat.
          */}
          {seats.map((player, idx) => {
            const isPhoneHero = phone && idx === 0;
            return (
              <PlayerSeat
                key={player?.id ?? `empty-${idx}`}
                player={player}
                isHero={player?.id === heroWallet || isPhoneHero}
                seatPos={seatPositions[idx] ?? seatPositions[0]!}
                isDealing={cardAnimations.dealingPhase === 'preflop'}
                heroPublicKey={heroWallet}
                compact={phone || display.isPhone || isHu}
                uiScale={phone ? Math.min(display.uiScale, 0.9) : display.uiScale}
                timer={gameState.timer}
                hideHole={isPhoneHero}
              />
            );
          })}

          {phone && (
            <button
              type="button"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['/api/tables', tableId] })}
              aria-label="Refresh table"
              style={{
                position: 'absolute', top: 8, right: 10, width: 36, height: 36,
                borderRadius: '50%', border: '1px solid rgba(255,255,255,0.15)',
                background: 'rgba(0,0,0,0.45)', color: '#93c5fd', display: 'grid', placeItems: 'center',
              }}
            >
              <RefreshCw size={16} />
            </button>
          )}
        </div>

        {/* ── Phone action dock (budbpkr-style) OR desktop action bar ── */}
        {phone ? (
          <div
            className="poker-action-bar poker-action-bar--phone"
            style={{
              width: '100%',
              marginTop: 'auto',
              background: 'linear-gradient(180deg, transparent 0%, rgba(5,8,16,0.92) 18%, #050810 100%)',
              padding: '8px 10px max(10px, env(safe-area-inset-bottom))',
              zIndex: 25,
            }}
          >
            {!isPlayerAtTable ? (
              <div style={{ textAlign: 'center', padding: '12px 8px' }}>
                <p style={{ color: 'rgba(255,255,255,0.55)', marginBottom: 10, fontSize: 13 }}>Not seated</p>
                <button
                  type="button"
                  onClick={() => setLocation('/lobby')}
                  data-testid="button-join-from-table"
                  style={{
                    width: '100%', minHeight: 52, border: 'none', borderRadius: 14,
                    background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)', color: '#fff',
                    fontWeight: 800, fontSize: 16, cursor: 'pointer',
                  }}
                >
                  Join from Lobby
                </button>
              </div>
            ) : (
              <>
                {heroBestHand && !heroPlayer?.sittingOut && (
                  <div className="poker-best-hand" data-testid="text-best-hand">
                    You have: <strong>{heroBestHand}</strong>
                  </div>
                )}
                {isPlayerAtTable && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <button
                      type="button"
                      data-testid="button-sit-out"
                      onClick={() =>
                        wsSendSitOut(!Boolean((heroSeat as { sittingOut?: boolean })?.sittingOut))
                      }
                      style={{
                        minHeight: 36,
                        padding: '0 14px',
                        borderRadius: 10,
                        border: '1px solid rgba(251,191,36,0.45)',
                        background: (heroSeat as { sittingOut?: boolean })?.sittingOut
                          ? 'linear-gradient(135deg,#166534,#22c55e)'
                          : 'rgba(0,0,0,0.55)',
                        color: '#fde68a',
                        fontWeight: 800,
                        fontSize: 12,
                        letterSpacing: '0.04em',
                        fontFamily: '"Bebas Neue", Impact, sans-serif',
                        cursor: 'pointer',
                      }}
                    >
                      {(heroSeat as { sittingOut?: boolean })?.sittingOut
                        ? 'SIT IN'
                        : 'SIT OUT'}
                    </button>
                    {(heroSeat as { sittingOut?: boolean })?.sittingOut && (
                      <span
                        style={{
                          alignSelf: 'center',
                          fontSize: 10,
                          color: 'rgba(251,191,36,0.85)',
                        }}
                      >
                        Skipping hands · idle kick after timeouts
                      </span>
                    )}
                  </div>
                )}
                {/* phonepoker3 dock: Your Hand | bet | Betting Controls */}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{
                      color: 'rgba(34,211,238,0.75)', fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>
                      Your Hand
                    </div>
                    {isMyTurn && gameState.timer?.deadlineAt && (
                      <TurnTimerBar
                        active
                        large
                        deadlineAt={gameState.timer.deadlineAt}
                        phase={gameState.timer.phase}
                        totalMs={
                          gameState.timer.phaseTotalMs ??
                          gameState.timer.shotClockMs
                        }
                        timeBankMs={heroPlayer?.timeBankMs}
                      />
                    )}
                    <div style={{
                      borderRadius: 10,
                      border: '1.5px solid rgba(34,211,238,0.45)',
                      background: 'rgba(8,24,48,0.55)',
                      padding: '8px 10px',
                      minWidth: 88,
                      minHeight: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {heroPlayer?.isFolded || heroPlayer?.sittingOut ? (
                        <FoldedCards size="sm" />
                      ) : (
                        <HoleCards
                          cards={(heroPlayer?.cards ?? []).map(c => ({ suit: c.suit, value: c.rank }))}
                          isHidden={false}
                          isDealing={cardAnimations.dealingPhase === 'preflop'}
                        />
                      )}
                    </div>
                  </div>

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 6, minWidth: 0 }}>
                    <div style={{
                      background: 'rgba(6,20,40,0.85)',
                      border: '1px solid rgba(34,211,238,0.35)',
                      borderRadius: 10,
                      padding: '6px 14px', color: '#e8c547', fontWeight: 800, fontSize: 15,
                    }}>
                      {effectiveBet.toFixed(0)} GBUX
                    </div>
                    {!canAct && (
                      <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                        {isWaiting ? 'Waiting for hand…' : 'Waiting…'}
                      </div>
                    )}
                  </div>

                  {/* Vertical bet controls */}
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 4,
                    flex: '0 0 auto', width: 72,
                  }}>
                    <div style={{
                      color: 'rgba(34,211,238,0.75)', fontSize: 9, fontWeight: 800,
                      letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'center',
                    }}>
                      Bet
                    </div>
                    {phoneBetPresets.map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        disabled={!canAct}
                        onClick={() => setBetValue(p.amt)}
                        style={{
                          minHeight: 36, borderRadius: 8, border: 'none',
                          background: p.label === 'All-in' ? 'rgba(59,130,246,0.85)' : 'rgba(55,65,90,0.9)',
                          color: '#fff', fontWeight: 800, fontSize: 12,
                          opacity: canAct ? 1 : 0.4, cursor: canAct ? 'pointer' : 'default',
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, marginTop: 2 }}>
                      <button
                        type="button"
                        disabled={!canAct}
                        onClick={() => setBetValue(Math.min(sliderMax, effectiveBet + stepAmt))}
                        style={{
                          width: 40, height: 36, borderRadius: 8, border: 'none',
                          background: 'rgba(55,65,90,0.95)', color: '#fff', display: 'grid', placeItems: 'center',
                          opacity: canAct ? 1 : 0.4,
                        }}
                      >
                        <Plus size={18} />
                      </button>
                      <input
                        type="range"
                        min={sliderMin}
                        max={sliderMax}
                        step={stepAmt}
                        value={effectiveBet}
                        disabled={!canAct}
                        onChange={e => setBetValue(Number(e.target.value))}
                        data-testid="input-bet"
                        className="phone-vertical-slider"
                        style={{
                          writingMode: 'vertical-lr' as const,
                          direction: 'rtl',
                          height: 88,
                          width: 28,
                          accentColor: '#3b82f6',
                          opacity: canAct ? 1 : 0.4,
                        }}
                      />
                      <button
                        type="button"
                        disabled={!canAct}
                        onClick={() => setBetValue(Math.max(sliderMin, effectiveBet - stepAmt))}
                        style={{
                          width: 40, height: 36, borderRadius: 8, border: 'none',
                          background: 'rgba(55,65,90,0.95)', color: '#fff', display: 'grid', placeItems: 'center',
                          opacity: canAct ? 1 : 0.4,
                        }}
                      >
                        <Minus size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Full-width FOLD / CALL / RAISE */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <button
                    type="button"
                    onClick={handleFold}
                    disabled={!canAct}
                    data-testid="button-fold"
                    style={{
                      minHeight: 52, border: 'none', borderRadius: 14,
                      background: canAct
                        ? 'linear-gradient(180deg, #b91c1c, #7f1d1d)'
                        : 'rgba(80,40,40,0.5)',
                      color: '#fff', fontWeight: 900, fontSize: 15, letterSpacing: 0.5,
                      cursor: canAct ? 'pointer' : 'default', opacity: canAct ? 1 : 0.45,
                    }}
                  >
                    FOLD
                  </button>
                  <button
                    type="button"
                    onClick={canCheck ? handleCheck : handleCall}
                    disabled={!canAct}
                    data-testid="button-call"
                    style={{
                      minHeight: 52, border: 'none', borderRadius: 14,
                      background: canAct
                        ? 'linear-gradient(180deg, #d97706, #92400e)'
                        : 'rgba(80,60,20,0.5)',
                      color: '#fff', fontWeight: 900, fontSize: 14, lineHeight: 1.15,
                      cursor: canAct ? 'pointer' : 'default', opacity: canAct ? 1 : 0.45,
                    }}
                  >
                    {canCheck ? (
                      'CHECK'
                    ) : isAllInCall ? (
                      'ALL-IN'
                    ) : (
                      <>
                        CALL
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 800 }}>
                          {callAmount.toFixed(0)}
                        </span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={hasToCall ? handleRaise : handleBet}
                    disabled={!canAct || (hasToCall ? effectiveBet <= gameState.currentBet : effectiveBet <= 0)}
                    data-testid={hasToCall ? 'button-raise' : 'button-bet'}
                    style={{
                      minHeight: 52, border: 'none', borderRadius: 14,
                      background: canAct
                        ? 'linear-gradient(180deg, #16a34a, #14532d)'
                        : 'rgba(30,60,40,0.5)',
                      color: '#fff', fontWeight: 900, fontSize: 12, lineHeight: 1.15,
                      cursor: canAct ? 'pointer' : 'default',
                      opacity: canAct ? 1 : 0.45, padding: '6px 4px',
                    }}
                  >
                    {raiseLabel}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div
              className="poker-action-bar poker-action-bar--prod"
              style={{
              borderColor: canAct ? 'rgba(234,179,8,0.4)' : undefined,
            }}>
              {isPlayerAtTable ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      type="button"
                      data-testid="button-sit-out-desktop"
                      onClick={() =>
                        wsSendSitOut(!Boolean(heroPlayer?.sittingOut))
                      }
                      style={{
                        minHeight: 32,
                        padding: '0 12px',
                        borderRadius: 8,
                        border: '1px solid rgba(251,191,36,0.4)',
                        background: heroPlayer?.sittingOut
                          ? 'linear-gradient(135deg,#166534,#22c55e)'
                          : 'rgba(0,0,0,0.45)',
                        color: '#fde68a',
                        fontWeight: 800,
                        fontSize: 11,
                        letterSpacing: '0.06em',
                        fontFamily: '"Bebas Neue", Impact, sans-serif',
                        cursor: 'pointer',
                      }}
                    >
                      {heroPlayer?.sittingOut ? 'SIT IN' : 'SIT OUT'}
                    </button>
                    {heroPlayer?.sittingOut && (
                      <span style={{ fontSize: 11, color: 'rgba(251,191,36,0.8)' }}>
                        Sitting out · kicked after idle / missed hands
                      </span>
                    )}
                  </div>
                  {heroBestHand && (
                    <div className="poker-best-hand" data-testid="text-best-hand">
                      You have: <strong>{heroBestHand}</strong>
                    </div>
                  )}
                  {!canAct && (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                      {isWaiting ? 'Waiting for next hand…' : 'Waiting for your turn…'}
                    </div>
                  )}
                  <div className="poker-act-row">
                    <ActionBtn label="FOLD" onClick={handleFold} disabled={!canAct} color="#ef4444" testId="button-fold" />
                    {canCheck ? (
                      <ActionBtn label="CHECK" onClick={handleCheck} disabled={!canAct} color="#4f46e5" testId="button-check" />
                    ) : (
                      <ActionBtn
                        label={isAllInCall ? 'ALL-IN' : 'CALL'}
                        sublabel={callAmount.toFixed(0)}
                        onClick={handleCall}
                        disabled={!canAct}
                        color="#16a34a"
                        testId="button-call"
                      />
                    )}
                    <ActionBtn label="ALL IN" onClick={handleAllIn} disabled={!canAct} color="#b91c1c" testId="button-allin" />
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>{hasToCall ? 'Raise to' : 'Bet'}</span>
                      <span style={{ color: '#fbbf24', fontWeight: 800, fontSize: 16 }}>{effectiveBet.toFixed(0)} <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 400 }}>GBUX</span></span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                      {potPresets.map(p => (
                        <button key={p.label} onClick={() => setBetValue(p.amt)} disabled={!canAct} style={{
                          background: betValue === p.amt ? 'rgba(234,179,8,0.25)' : 'rgba(255,255,255,0.07)',
                          border: betValue === p.amt ? '1px solid rgba(234,179,8,0.6)' : '1px solid rgba(255,255,255,0.12)',
                          borderRadius: 6, color: betValue === p.amt ? '#fbbf24' : 'rgba(255,255,255,0.7)',
                          fontSize: 11, fontWeight: 700, padding: '4px 10px', cursor: !canAct ? 'default' : 'pointer', opacity: !canAct ? 0.4 : 1,
                        }}>{p.label}</button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <input type="range" min={sliderMin} max={sliderMax} step={Math.max(1, Math.round(bigBlind / 2))} value={effectiveBet} disabled={!canAct} onChange={e => setBetValue(Number(e.target.value))} data-testid="input-bet" style={{ flex: 1, accentColor: '#eab308', height: 4, opacity: !canAct ? 0.4 : 1 }} />
                      <ActionBtn
                        label={hasToCall ? 'RAISE' : 'BET'}
                        onClick={hasToCall ? handleRaise : handleBet}
                        disabled={!canAct || (hasToCall ? effectiveBet <= gameState.currentBet : effectiveBet <= 0)}
                        color={hasToCall ? '#d97706' : '#0284c7'}
                        testId={hasToCall ? 'button-raise' : 'button-bet'}
                      />
                    </div>
                    {canAct && hasToCall && pot > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 16, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                        <span>To call <span style={{ color: '#4ade80' }}>{callAmount.toFixed(0)}</span></span>
                        <span>Pot odds <span style={{ color: '#60a5fa' }}>{potOdds}%</span></span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 12 }}>You are not seated at this table</p>
                  <motion.button onClick={() => setLocation('/lobby')} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} data-testid="button-join-from-table"
                    style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: 10, color: 'white', padding: '10px 28px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                    Return to Lobby to Join
                  </motion.button>
                </div>
              )}
            </div>
            {/* Bottom info rects removed — stakes/players live in header meta */}
          </>
        )}
      </div>

      {!phone && (
        <TableRightRail
          players={(table?.players ?? seats.filter(Boolean)) as Array<{
            id?: string;
            username?: string;
            chips?: number;
            isActive?: boolean;
          }>}
          maxPlayers={table?.maxPlayers ?? 6}
        />
      )}

      {/*
        BUDB voice↔voice ONLY on HU VS BUDB (ai agent seat).
        Racalvin voice↔voice is blackjack-only — never here.
      */}
      {tableId === BUDB_CHARACTER.vsTableId && (
        <BudbAvatarEmbed compact={phone} autoStart />
      )}
    </div>
  );
}
